<template>
  <div :id="`${entityType}_lists_select`" class="form_col">
    <span :class="titleClasses">Lists</span>
    <a-select
      v-model:value="selectedLists"
      class="w-full"
      placeholder="Add to Lists"
      mode="multiple"
      :show-search="false"
      show-arrow
      @change="handleSelectionChange"
      :disabled="disabled"
      @dropdown-visible-change="handleDropdownVisibleChange"
      :getPopupContainer="(trigger) => trigger.parentElement"
    >
      <template #suffixIcon>
        <ThemeImage
          :icon="imageNames.BOTTOM_ARROW"
          alt="bottom_white_arrow"
          class="size-4"
        />
      </template>
      <template #dropdownRender>
        <AddToListDropdown
          :width="'w-full'"
          ref="addToListDropdownRef"
          :source="entityType"
          :entity-lists="lists"
          :show-entity-create="showCreateListOption"
          :hide-dropdown-wrapper="true"
          @update:show-entity-create="showCreateListOption = $event"
          @entity-list-created="handleEntityListCreated"
          v-model:selectedItems="selectedLists"
          select-mode
        />
      </template>
      <a-select-option
        v-for="(list, index) in lists"
        :key="index"
        :value="list?.id"
        :label="list?.list_name || ''"
        class="hidden"
      >
        {{ list?.list_name }}
      </a-select-option>
    </a-select>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from "vue";
import { message } from "ant-design-vue";

import {
  createAntSelectBackspaceDisabler,
  toGetAllEntityLists,
} from "@/src/utils/utils.js";
import AddToListDropdown from "@/src/components/custom_table_components/AddToListDropdown.vue";

import { imageNames } from "@/src/constants/imageConstants.js";

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  entityType: {
    type: String,
    required: true,
  },
  disabled: {
    type: Boolean,
    required: false,
    default: false,
  },
  titleClasses: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["update:modelValue"]);

const lists = ref([]);
const showCreateListOption = ref(false);
const addToListDropdownRef = ref(null);

const selectedLists = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const listsBackspaceDisabler = createAntSelectBackspaceDisabler(
  `#${props.entityType}_lists_select .ant-select`
);

const handleSelectionChange = (value) => {
  emit("update:modelValue", value);
};

const handleEntityListCreated = async (data) => {
  if (data?.listId) {
    // Add temporary option immediately so the chip shows the name, not the raw ID
    if (data.list_name) {
      lists.value = [...lists.value, { id: data.listId, list_name: data.list_name }];
    }
    const newValue = [...props.modelValue, data.listId];
    emit("update:modelValue", newValue);
  }
  await fetchLists();
};

const handleDropdownVisibleChange = (visible) => {
  if (!visible) {
    addToListDropdownRef.value?.resetSearch();
    showCreateListOption.value = false;
  }
};

const fetchLists = async () => {
  try {
    lists.value = await toGetAllEntityLists(props.entityType);
  } catch (error) {
    console.error("Error fetching lists:", error);
    message.error("Failed to fetch lists");
  }
};

onMounted(() => {
  fetchLists();
  try {
    if (listsBackspaceDisabler) {
      listsBackspaceDisabler.observe();
    }
  } catch (error) {
    console.error("Failed to initialize backspace disabler:", error);
  }
});

onUnmounted(() => {
  try {
    if (listsBackspaceDisabler) {
      listsBackspaceDisabler.disconnect();
    }
  } catch (error) {
    console.error("Failed to cleanup backspace disabler:", error);
  }
});
</script>
